import { createDialogBodyOverlayScrollCoordinator } from './dialogOverlayPositioning.js';
import { sizePickerOverlay } from './pickerOverlayLayout.js';

// PrimeVue positions the real overlay; constrain it to the visible viewport
// when a touch keyboard reduces the space available to its scrollable list.
export function createTouchPickerOverlayCoordinator() {
  const dialogCoordinator = createDialogBodyOverlayScrollCoordinator();
  let current = null;
  let frame = null;
  let resizeObserver = null;
  let originalListMaxHeight = '';

  const align = () => {
    if (!current?.isVisible()) return false;
    const { overlay, control, alignOverlay } = current;
    const viewport = window.visualViewport;
    const gutter = 8;
    const left = (viewport?.offsetLeft || 0) + gutter;
    const top = (viewport?.offsetTop || 0) + gutter;
    const width = Math.max(0, (viewport?.width || window.innerWidth) - gutter * 2);
    const height = Math.max(0, (viewport?.height || window.innerHeight) - gutter * 2);
    sizePickerOverlay(overlay, control);

    const list = overlay.querySelector('[data-pc-section="listcontainer"]');
    if (list) {
      const chromeHeight = overlay.offsetHeight - list.offsetHeight;
      const availableHeight = Math.max(0, height - chromeHeight);
      list.style.maxHeight = originalListMaxHeight
        ? `min(${originalListMaxHeight}, ${availableHeight}px)`
        : `${availableHeight}px`;
    }

    alignOverlay();
    sizePickerOverlay(overlay, control);
    const rect = overlay.getBoundingClientRect();
    const x = Math.max(left, Math.min(rect.left, left + width - rect.width));
    const y = Math.max(top, Math.min(rect.top, top + height - rect.height));
    // PrimeVue uses logical horizontal insets. Writing `left` would override
    // its anchor even when no correction is necessary (and stretch RTL panels).
    const inset = overlay.style.insetInlineStart
      ? 'insetInlineStart'
      : overlay.style.insetInlineEnd
        ? 'insetInlineEnd'
        : 'left';
    const rtl = window.getComputedStyle(overlay).direction === 'rtl';
    const sign =
      inset === 'insetInlineStart' ? (rtl ? -1 : 1) : inset === 'insetInlineEnd' && !rtl ? -1 : 1;
    overlay.style[inset] =
      `${(Number.parseFloat(overlay.style[inset]) || 0) + (x - rect.left) * sign}px`;
    overlay.style.top = `${(Number.parseFloat(overlay.style.top) || 0) + y - rect.top}px`;
    return true;
  };

  const schedule = () => {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(() => {
      frame = null;
      align();
    });
  };

  const unbind = () => {
    dialogCoordinator.unbind();
    resizeObserver?.disconnect();
    resizeObserver = null;
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
      if (frame !== null) window.cancelAnimationFrame(frame);
    }
    frame = null;
    current = null;
  };

  return {
    align,
    bind(options) {
      unbind();
      if (!options?.overlay || !options.control || !options.isVisible?.()) return;
      current = options;
      originalListMaxHeight =
        options.overlay.querySelector('[data-pc-section="listcontainer"]')?.style.maxHeight || '';
      dialogCoordinator.bind({ ...options, alignOverlay: align });
      window.addEventListener('resize', schedule);
      window.visualViewport?.addEventListener('resize', schedule);
      window.visualViewport?.addEventListener('scroll', schedule);
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(schedule);
        resizeObserver.observe(options.overlay);
        resizeObserver.observe(options.control);
      }
      align();
    },
    unbind,
  };
}
